/**
 * @file CountrySelection.jsx
 * @author Rishikesh
 * @date 2024-09-09
 * @description Component of users to manage country
 */
import React from "react";
import {
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import { ContentPasteSearchOutlined } from "@mui/icons-material";

export const CountrySelection = ({
  isLoading,
  countryList,
  selectedCountry,
  setSelectedCountry,
  courseCountry,
  setCourseCountry,
}) => {
  const [searchQuery, setSearchQuery] = React.useState("");

  const filteredCountries = countryList.filter((item) =>
    item.country_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isCountryChecked = (countryId) => {
    return courseCountry?.find((item) => item.countryid === countryId.id)
      ? true
      : false;
  };

  const handleToggleCountry = (countryId) => () => {
    const existingCountry = courseCountry.find(
      (item) => item.countryid === countryId
    );

    if (existingCountry) {
      const updatedCountryList = courseCountry.filter(
        (item) => item.countryid !== countryId
      );

      if (selectedCountry?.id === existingCountry.countryid) {
        setSelectedCountry();
      }

      setCourseCountry(updatedCountryList);
    } else {
      setCourseCountry([
        ...courseCountry,
        { countryid: countryId, courses: [] },
      ]);
    }
  };

  return (
    <Box
      flex={1}
      borderRadius="12px"
      px="8px"
      py={3}
      bgcolor="#F7F7F7"
      display="flex"
      flexDirection="column"
    >
      <Stack
        pb={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {isLoading ? (
          <Skeleton variant="rounded" width={140} height={24} />
        ) : (
          <Typography fontWeight={400} fontSize={18} color="#222222">
            Countries
          </Typography>
        )}
      </Stack>
      <Box>
        {isLoading ? (
          <Skeleton variant="rounded" width={180} height={18} />
        ) : (
          <Typography color="textDisabled">
            Choose countries that you want to assign to this employee
          </Typography>
        )}
        {isLoading ? (
          <Skeleton
            variant="rounded"
            sx={{ mt: 3, mb: 1 }}
            width={"100%"}
            height={40}
          />
        ) : (
          <TextField
            id="outlined-start-adornment"
            placeholder="Search country"
            fullWidth
            className="inputMdRounded"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ bgcolor: "transparent" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <ContentPasteSearchOutlined />
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
      </Box>
      <List dense sx={{ width: "100%", flex: 1, overflowY: "auto" }}>
        {isLoading
          ? Array.from({ length: 7 }).map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                sx={{ mt: 1 }}
                width={"100%"}
                height={32}
              />
            ))
          : filteredCountries?.map((item) => (
              <ListItem
                key={item.id}
                secondaryAction={
                  <Checkbox
                    edge="end"
                    onChange={handleToggleCountry(item.id)}
                    checked={isCountryChecked(item)}
                    sx={{
                      color: selectedCountry?.id === item.id ? "white" : "gray",
                      "&.Mui-checked": {
                        color:
                          selectedCountry?.id === item.id ? "white" : "#3A80F7",
                      },
                      display:
                        isCountryChecked(item) ||
                        selectedCountry?.id === item.id
                          ? "inline-flex"
                          : "none",
                    }}
                  />
                }
                disablePadding
                sx={{
                  bgcolor:
                    selectedCountry?.id === item.id
                      ? "#3A80F7"
                      : isCountryChecked(item)
                      ? "#E4EBF4"
                      : "transparent",
                  "&:hover": {
                    "& .MuiCheckbox-root": {
                      display: "inline-flex",
                    },
                  },
                }}
              >
                <ListItemButton
                  onClick={() => {
                    setSelectedCountry(item);
                    if (!isCountryChecked(item)) {
                      handleToggleCountry(item.id)();
                    }
                  }}
                >
                  <ListItemText
                    sx={{
                      color:
                        selectedCountry?.id === item.id ? "white" : "black",
                    }}
                    primary={item.country_name}
                  />
                </ListItemButton>
              </ListItem>
            ))}
      </List>
    </Box>
  );
};
